import ReactModal from 'react-modal';
import { useTranslation } from "react-i18next";

import { Button, Input, CircleIcon } from "@atoms";
import { Layout } from "@layouts";
import { styles as globalStyles } from "../../../styles/styles";
import { ImageUploader } from "@molecules";
import { provinceOptions, countryOptions } from "./location-data";
import { Info } from "lucide-react";

const colors = require("@theme/colors") as Record<string, any>;

export type ProfilePageTemplateProps = {
  partnerData: any;
  onEditAddressClicked: () => void;
  onEditDescriptionClicked: () => void;
  onEditCompanyClicked: () => void;
  handleInputChangeCompany: any;
  handleInputChangeDescription: any
  handleInputChangeAddress: any;
  handleInputChangeCountry: any;
  handleInputChangeProvince: any;
  handleSubmitCompany: any;
  handleSubmitDescription: any;
  handleSubmitAddress: any;
  valuesCompany: any;
  valuesDescription: any;
  valuesAddress: any;
  province: string;
  country: string;
  errorsCompany: any;
  errorsDescription: any;
  errorsAddress: any;
  modalChangeCompany: boolean;
  modalChangeDescription: boolean;
  modalChangeAddress: boolean;
  closeModal: () => void;
  isLoading: boolean;
}

export const ProfilePageTemplate = ({
  partnerData,
  onEditAddressClicked,
  onEditDescriptionClicked,
  onEditCompanyClicked,
  handleInputChangeCompany,
  handleInputChangeDescription,
  handleInputChangeAddress,
  handleInputChangeCountry,
  handleInputChangeProvince,
  handleSubmitCompany,
  handleSubmitDescription,
  handleSubmitAddress,
  valuesCompany,
  valuesDescription,
  valuesAddress,
  province,
  country,
  errorsCompany,
  errorsDescription,
  errorsAddress,
  modalChangeCompany,
  modalChangeDescription,
  modalChangeAddress,
  closeModal,
  isLoading
}: ProfilePageTemplateProps): JSX.Element => {
  const { t } = useTranslation();

  const { company } = valuesCompany;
  const { company: companyError } = errorsCompany;

  const { description } = valuesDescription;
  const { description: descriptionError } = errorsDescription;

  // const { street, city, country, postalCode } = valuesAddress;
  const { street, city, postalCode } = valuesAddress;
  const { street: streetError, city: cityError, country: countryError, province: provinceError, postalCode: postalCodeError  } = errorsAddress;

  const isAddressEmpty = partnerData?.address === null || partnerData?.address === undefined;
  const { address } = partnerData;

  const stateProvinceOptions: any = country !== "CA" ? provinceOptions.US : provinceOptions.CA;
  
  return (
    <Layout
      variant="dashboard"
      header={t("nav.organization")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("profile.subheader")}
        </div>}
    >
      <div>
        <div className="flex flex-col justify-between max-w-5xl w-full py-1 mr-4">
          <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6 mt-3 border-b-1 border-black border-grey-300">
            <div className="flex flex-row items-center gap-3 w-full">
              <div className="section-title">{t("profile.companyInformation")}</div>
            </div>
            <div className="flex flex-col gap-6 lg:flex-row lg:justify-start">
              <div className="flex flex-col items-start py-5">
                <div className="">
                  <ImageUploader />
                </div>
              </div>
              <div className="flex flex-col px-6 w-full">
                <div className="w-full divide-y divide-neutral-light">
                  <div className="flex flex-col gap-3 py-4 sm:flex-row sm:justify-between sm:items-center">
                    <div className="space-y-2">
                      <div className="text-black text-md font-medium">
                        {t("profile.companyName")}
                      </div>
                      <div className="text-black text-lg">
                        {partnerData?.organization}
                      </div>
                    </div>
                    <div className="min-w-max">
                      <div>
                        <Button
                          onClick={onEditCompanyClicked}
                          variant="secondary"
                          label={t("profile.editCompany")}
                          autosize
                          color="#FFFFFF"
                          hoverColor="#FFE8D6"
                          textColor={colors.primary.medium}
                          borderColor={colors.primary.medium}
                          fontWeight={600}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 py-4 sm:flex-row sm:justify-between sm:items-center">
                    <div className="space-y-2 pr-3">
                      <div className="text-black text-md font-medium">
                        {t("profile.description")}
                      </div>
                      <div className="text-black text-lg">
                        {partnerData?.description}
                      </div>
                    </div>
                    <div className="min-w-max">
                      <div>
                        <Button
                          onClick={onEditDescriptionClicked}
                          variant="secondary"
                          label={t("profile.editDescription")}
                          autosize
                          color="#FFFFFF"
                          hoverColor="#FFE8D6"
                          textColor={colors.primary.medium}
                          borderColor={colors.primary.medium}
                          fontWeight={600}
                        />
                      </div>
                    </div>     
                  </div>
                  <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-between sm:items-center">
                    <div className="w-full sm:w-2/3 space-y-2">
                      <div className="text-black text-md font-medium">
                        {t("profile.primaryAddress")}
                      </div>
                      {isAddressEmpty ?
                        <div className="">
                          <div className="text-attention-darkest mt-2 p-3 pl-6 rounded-xl bg-attention-lightest">
                          {t("profile.addressAlert", { subscriptionLabel: t("nav.subscription") })}
                          </div>
                        </div>
                        :
                        <div className="flex flex-col w-full">
                          <div className="flex flex-row w-full space-between">
                            <div className="w-full font-normal text-lg">
                              <div>{address?.street}</div>
                              <div>{`${address?.city} ${address?.province} ${address?.postalCode}`}</div>
                              <div>{address?.country}</div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                    <div className="min-w-max">
                      <div>
                        <Button
                          onClick={onEditAddressClicked}
                          variant="secondary"
                          label={isAddressEmpty ? t("profile.addAddress") : t("profile.editAddress")}
                          autosize
                          color="#FFFFFF"
                          hoverColor="#FFE8D6"
                          textColor={colors.primary.medium}
                          borderColor={colors.primary.medium}
                          fontWeight={600}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Change company name */}
          <ReactModal
            isOpen={modalChangeCompany}
            onRequestClose={closeModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("profile.modal.companyLabel")}
          >
            <div className="font-medium text-neutral-darkest text-xl pt-4 pb-8">{t("profile.modal.companyTitle")}</div>
            <form className="pb-6" onSubmit={handleSubmitCompany}>
              <div className="mb-3">
                <Input
                  name="company"
                  variant="text"
                  onChange={handleInputChangeCompany}
                  placeholder={t("profile.companyName")}
                  value={company}
                  errorMessage={companyError}
                />
              </div>
              <div className="flex flex-row place-content-end pt-4">
                <div className="min-w-[220px]">   
                  <Button
                    type="submit"
                    variant="primary"
                    label={t("profile.saveChanges")}
                    stretch
                    className="whitespace-nowrap"
                    paddingXClassName="px-5"
                    color="primary-medium"
                    textWeight="semibold"
                    loading={isLoading}
                  /> 
                </div>                
              </div>    
            </form>
          </ReactModal>

          {/* Change company description */}
          <ReactModal
            isOpen={modalChangeDescription}
            onRequestClose={closeModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("profile.modal.descriptionLabel")}
          >
            <div className="font-medium text-neutral-darkest text-xl pt-4 pb-8">{t("profile.modal.descriptionTitle")}</div>
            <form className="pb-6" onSubmit={handleSubmitDescription}>
              <div className="mb-3">
                <Input
                  name="description"
                  variant="textArea"
                  onChange={handleInputChangeDescription}
                  placeholder={t("profile.description")}
                  value={description}
                  errorMessage={descriptionError}
                  height={128}
                />
              </div>
              <div className="flex flex-row place-content-end pt-4">
                <div className="min-w-[220px]">   
                  <Button
                    type="submit"
                    variant="primary"
                    label={t("profile.saveChanges")}
                    stretch
                    className="whitespace-nowrap"
                    paddingXClassName="px-5"
                    color="primary-medium"
                    textWeight="semibold"
                    loading={isLoading}
                  /> 
                </div>                
              </div>    
            </form>
          </ReactModal>

          {/* Change company address */}
          <ReactModal
            isOpen={modalChangeAddress}
            onRequestClose={closeModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
            contentLabel={t("profile.modal.addressLabel")}
          >
            <div className="font-medium text-neutral-darkest text-xl pt-4">{t("profile.primaryAddress")}</div>
            <div className="font-normal text-neutral-dark text-base pt-1 pb-8">{t("profile.modal.addressSubtitle")}</div>
            <form className="pb-6" onSubmit={handleSubmitAddress}>
              <div className="mb-3">
                <Input
                  name="street"
                  variant="text"
                  onChange={handleInputChangeAddress}
                  placeholder={t("profile.fields.street")}
                  value={street}
                  errorMessage={streetError}
                />
              </div>
              <div className="mb-3">
                <Input
                  name="city"
                  variant="text"
                  onChange={handleInputChangeAddress}
                  placeholder={t("profile.fields.city")}
                  value={city}
                  errorMessage={cityError}
                />
              </div>                  
              <div className="mb-3">
                <Input
                  type="provinceDropdown"
                  name="province"
                  variant="dropdown"
                  options={stateProvinceOptions}
                  onChange={handleInputChangeProvince}
                  placeholder={address?.province ? stateProvinceOptions.find((item: any) => item.value === province) : null}
                  value={province}
                  errorMessage={provinceError}
                />
              </div>
              {/* <div className="mb-3">
                <Input
                  type="countryDropdown"
                  name="country"
                  variant="dropdown"
                  options={countryOptions}
                  onChange={handleInputChangeCountry}
                  placeholder={address?.country ? countryOptions.find(item => item.value === country) : countryOptions[0]}
                  value={country}
                  errorMessage={countryError}
                />
              </div>
              */}
              <div className="mb-3">
                <Input
                  name="country"
                  variant="text"
                  onChange={()=>{}}
                  placeholder={t("profile.fields.country")}
                  value={country}
                  errorMessage={countryError}
                />
              </div> 
              <div className="mb-3">
                <Input
                  name="postalCode"
                  variant="text"
                  onChange={handleInputChangeAddress}
                  placeholder={country === "CA" ? t("profile.fields.postalCode") : t("profile.fields.zipCode")}
                  value={postalCode}
                  errorMessage={postalCodeError}
                />
              </div>
              <div className="flex flex-row place-content-end pt-4">
                <div className="min-w-[220px]">   
                  <Button
                    type="submit"
                    variant="primary"
                    label={t("profile.saveChanges")}
                    stretch
                    className="whitespace-nowrap"
                    paddingXClassName="px-5"
                    color="primary-medium"
                    textWeight="semibold"
                    loading={isLoading}
                  /> 
                </div>                
              </div>    
            </form>
          </ReactModal>
        </div>
        
      </div>
    </Layout>
  );
};
